/**
 * Звуковая обратная связь на значимых событиях.
 *
 * Звук стоит ровно на трёх моментах, которые DESIGN_GUIDELINES.md §6.9 называет
 * значимыми: взаимный мэтч, отправка суперлайка, успешная оплата. Там же
 * записано, почему список именно такой: «если всё вибрирует, пользователь
 * выключит вибрацию совсем». К звуку это относится ещё сильнее — приложением
 * пользуются в метро, в офисе и в гостях, и лишний «дзынь» там не обратная
 * связь, а неловкость. Свайпы, нажатия, переходы и входящие сообщения звука
 * не имеют намеренно.
 *
 * Почему Web Audio, а не `new Audio()`: у элемента `<audio>` на iOS свойство
 * volume только для чтения, старт даёт заметную задержку, а два звука подряд
 * он переживает плохо. Через AudioContext файл декодируется один раз в буфер
 * и дальше играет мгновенно и с предсказуемой громкостью.
 *
 * Автоплей: браузер не даёт воспроизвести звук, пока пользователь ничего не
 * нажал, — свежесозданный AudioContext приходит в состоянии "suspended".
 * Поэтому контекст создаётся и прогревается на первом же касании экрана, см.
 * primeSoundOnFirstGesture(). До первого касания playSound() тихо ничего не
 * делает, и это ровно то, что нужно: до первого касания сообщать нечего.
 */

export type SoundName = "match" | "superlike" | "purchase";

const SOURCES: Record<SoundName, string> = {
    match: "/sounds/match.mp3",
    superlike: "/sounds/superlike.mp3",
    purchase: "/sounds/purchase.mp3",
};

/**
 * Минимальный интервал между повторами одного и того же звука.
 * Страхует от дублей: эффекты в React StrictMode вызываются дважды, а
 * realtime-сокет может прислать одно и то же событие мэтча повторно.
 */
const THROTTLE_MS = 600;

/**
 * Ключ настройки. Хранится локально, а не на сервере, осознанно:
 *  - звук — свойство устройства, а не аккаунта: с наушниками дома и на
 *    совещании с телефоном на столе человек хочет разного;
 *  - настройки с сервера приезжают асинхронно, и до ответа флаг был бы true —
 *    то есть выключивший звук всё равно слышал бы его после каждого холодного
 *    старта, пока грузится профиль. localStorage читается синхронно, поэтому
 *    выключенный звук выключен с первой миллисекунды.
 * Префикс rl_ — как у остальных локальных настроек (rl_show_onboarding_guide).
 */
const STORAGE_KEY = "rl_sound_enabled";

function readStoredPreference(): boolean {
    try {
        // Значение по умолчанию — включено: звук на трёх событиях в год
        // пользования никого не разбудит, а выключить его — одно касание в
        // настройках. Молчащее по умолчанию приложение эту функцию просто
        // никому не показало бы.
        return localStorage.getItem(STORAGE_KEY) !== "0";
    } catch {
        // localStorage недоступен в приватном режиме и при жёстких настройках
        // приватности — работаем со значением по умолчанию.
        return true;
    }
}

let enabled = readStoredPreference();
let ctx: AudioContext | null = null;
let primed = false;
const buffers = new Map<SoundName, AudioBuffer>();
const lastPlayedAt = new Map<SoundName, number>();

/** Включить/выключить звук и запомнить выбор на этом устройстве. */
export function setSoundEnabled(value: boolean): void {
    enabled = value;
    try {
        localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
    } catch {
        // Не сохранилось — звук всё равно переключится на эту сессию.
    }
}

export function isSoundEnabled(): boolean {
    return enabled;
}

function createContext(): AudioContext | null {
    if (typeof window === "undefined") return null;
    const Ctor =
        window.AudioContext ??
        (window as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
    if (!Ctor) return null;
    try {
        return new Ctor();
    } catch {
        // Safari бросает, если контекстов уже слишком много. Звук не критичен.
        return null;
    }
}

async function loadBuffer(target: AudioContext, name: SoundName): Promise<void> {
    if (buffers.has(name)) return;
    try {
        const res = await fetch(SOURCES[name]);
        if (!res.ok) return;
        const raw = await res.arrayBuffer();
        buffers.set(name, await target.decodeAudioData(raw));
    } catch {
        // Файл не доехал или браузер не смог его раскодировать — приложение
        // продолжает работать молча, это осознанная деградация.
    }
}

/**
 * Создать контекст и заранее раскодировать все звуки.
 * Вызывается на первом жесте пользователя; повторные вызовы безвредны.
 */
export function warmUpSound(): void {
    if (primed) return;
    primed = true;
    ctx = createContext();
    if (!ctx) return;
    // resume() обязателен: без жеста контекст создаётся "suspended", и первый
    // же звук ушёл бы в тишину.
    void ctx.resume().catch(() => {});
    for (const name of Object.keys(SOURCES) as SoundName[]) {
        void loadBuffer(ctx, name);
    }
}

/**
 * Однократно повесить прогрев на первый жест пользователя.
 * Возвращает функцию отписки.
 */
export function primeSoundOnFirstGesture(): () => void {
    if (typeof window === "undefined") return () => {};
    const events = ["pointerdown", "touchstart", "keydown"] as const;
    const detach = () => {
        for (const event of events) window.removeEventListener(event, onGesture);
    };
    const onGesture = () => {
        warmUpSound();
        detach();
    };
    // Вешаем даже при выключенном звуке: пользователь может включить его в
    // настройках позже, и к этому моменту всё уже должно быть прогрето —
    // второго «первого жеста» не будет.
    for (const event of events) window.addEventListener(event, onGesture, {passive: true});
    return detach;
}

/**
 * Проиграть звук. Никогда не бросает и ничего не делает, если звук выключен,
 * ещё не прогрет или тот же звук уже играл только что.
 */
export function playSound(name: SoundName): void {
    if (!enabled || !ctx) return;

    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const last = lastPlayedAt.get(name);
    if (last !== undefined && now - last < THROTTLE_MS) return;

    const buffer = buffers.get(name);
    if (!buffer) return;

    try {
        if (ctx.state === "suspended") void ctx.resume().catch(() => {});
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start();
        lastPlayedAt.set(name, now);
    } catch {
        // Контекст мог закрыться вместе со вкладкой — звук не повод падать.
    }
}

/** Взаимный мэтч. */
export function soundMatch(): void {
    playSound("match");
}

/** Суперлайк отправлен. */
export function soundSuperLike(): void {
    playSound("superlike");
}

/** Оплата подтверждена. */
export function soundPurchase(): void {
    playSound("purchase");
}

/** Только для тестов: вернуть модуль в исходное состояние. */
export function __resetSoundForTests(): void {
    enabled = readStoredPreference();
    ctx = null;
    primed = false;
    buffers.clear();
    lastPlayedAt.clear();
}
